<template>
  <div
    class="predix-result-tabs sticky z-[15] border-b border-gray-200 bg-white/90 py-0 backdrop-blur-sm dark:border-white/10 dark:bg-gray-950/95"
    style="top: 64px; position: sticky;"
    role="tablist"
  >
    <div
      class="predix-result-tabs__row"
      style="display: flex; flex-wrap: nowrap; align-items: center; justify-content: space-between; width: 100%; box-sizing: border-box;"
    >
      <div
        class="predix-result-tabs__list"
        style="display: flex; flex-wrap: nowrap; align-items: center; gap: 4px; min-width: 0; flex: 1 1 auto;"
      >
        <button
          v-for="(tab, index) in tabs"
          :key="tab.id"
          type="button"
          role="tab"
          :data-tour="tab.tourId"
          class="predix-result-tab relative inline-flex items-center gap-2 px-5 py-3 text-sm font-[inherit] leading-snug transition-colors duration-200 border-b-2 border-transparent -mb-px bg-transparent"
          :class="tabClass(index)"
          :aria-selected="interactive && value === index"
          :disabled="!interactive"
          @click="onTabClick(index)"
        >
          <span
            class="predix-result-tab__icon inline-flex shrink-0 items-center justify-center"
            :class="iconClass(index)"
          >
            <icon :id="tab.icon"/>
          </span>
          {{ tab.label }}
        </button>
      </div>

      <div
        class="predix-result-tabs__actions"
        style="display: flex; align-items: center; flex: 0 0 auto; margin-left: 24px; padding-bottom: 6px;"
      >
        <button
          v-if="showFavorite"
          type="button"
          class="predix-export-btn"
          :disabled="!canFavorite"
          :title="canFavorite ? 'Save this alloy' : 'Run a Main DB prediction first to save this alloy'"
          @click="$emit('favorite-save')"
        >
          <v-icon small class="predix-export-btn__icon" aria-hidden="true">mdi-star-plus</v-icon>
          Save alloy
        </button>
        <button
          type="button"
          class="predix-export-btn"
          :disabled="!canReport"
          :title="canReport ? 'Export a shareable report' : 'Run a prediction first to enable Export'"
          @click="$emit('report')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="predix-export-btn__icon" aria-hidden="true">
            <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.69L6.3 8.49a.75.75 0 0 0-1.1 1.02l4.25 4.5a.75.75 0 0 0 1.1 0l4.25-4.5a.75.75 0 0 0-1.1-1.02l-2.95 3.12V2.75Z" />
            <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
          </svg>
          Export
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from "@/components/Icon";

export default {
  name: "PredixResultTabs",
  components: { Icon },
  props: {
    value: {
      type: Number,
      default: 0,
    },
    interactive: {
      type: Boolean,
      default: true,
    },
    canReport: {
      type: Boolean,
      default: false,
    },
    canFavorite: {
      type: Boolean,
      default: false,
    },
    showFavorite: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tabs: [
        { id: "predictions", label: "Alloy Predictions", icon: "magnify", tourId: "predix-alloy-predictions" },
        { id: "comparison", label: "Alloy Comparison", icon: "alloy-comparison", tourId: "predix-alloy-comparison" },
      ],
    };
  },
  methods: {
    tabClass(index) {
      const active = this.interactive && this.value === index;
      return [
        active ? "predix-result-tab--active font-semibold" : "predix-result-tab--idle font-medium",
        this.interactive ? "cursor-pointer" : "cursor-default pointer-events-none predix-result-tab--disabled",
      ];
    },
    iconClass(index) {
      return this.interactive && this.value === index
        ? "predix-result-tab__icon--active"
        : "predix-result-tab__icon--idle";
    },
    onTabClick(index) {
      if (!this.interactive || index === this.value) return;
      this.$emit("input", index);
    },
  },
};
</script>

<style lang="scss">
.predix-result-tabs .predix-result-tab {
  font-family: var(--font-inter, "Inter"), var(--font-sans, ui-sans-serif, system-ui, sans-serif);
  letter-spacing: 0;
}

.predix-result-tabs .predix-result-tab--disabled {
  opacity: 0.72;
}

.predix-result-tabs .predix-result-tab--disabled.predix-result-tab--idle {
  color: rgb(156, 163, 175) !important;
}

html.dark .predix-result-tabs .predix-result-tab--disabled.predix-result-tab--idle {
  color: rgb(107, 114, 128) !important;
}

.predix-result-tabs .predix-result-tab--disabled .predix-result-tab__icon--idle {
  color: rgb(156, 163, 175) !important;
}

html.dark .predix-result-tabs .predix-result-tab--disabled .predix-result-tab__icon--idle {
  color: rgb(107, 114, 128) !important;
}

.predix-result-tabs .predix-result-tab--active {
  color: var(--v-primary-base) !important;
  border-bottom-color: var(--v-primary-base) !important;
}

.predix-result-tabs .predix-result-tab--idle {
  color: rgb(107, 114, 128) !important;
}

.predix-result-tabs .predix-result-tab--idle:hover:not(:disabled) {
  color: rgb(55, 65, 81) !important;
}

html.dark .predix-result-tabs .predix-result-tab--idle {
  color: rgb(156, 163, 175) !important;
}

html.dark .predix-result-tabs .predix-result-tab--idle:hover:not(:disabled) {
  color: rgb(229, 231, 235) !important;
}

.predix-result-tabs .predix-result-tab__icon--active {
  color: var(--v-primary-base) !important;
}

.predix-result-tabs .predix-result-tab__icon--idle {
  color: rgb(107, 114, 128) !important;
}

html.dark .predix-result-tabs .predix-result-tab__icon--idle {
  color: rgb(156, 163, 175) !important;
}

.predix-result-tabs .predix-result-tab__icon svg.icon {
  fill: currentColor;
}

.predix-result-tabs {
  width: 100%;
  box-sizing: border-box;
}

.predix-result-tabs__row {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  justify-content: space-between !important;
  width: 100% !important;
  box-sizing: border-box;
  gap: 1.5rem;
}

.predix-result-tabs__list {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  flex: 1 1 auto;
}

.predix-result-tabs__actions {
  display: flex !important;
  align-items: center;
  gap: 0.5rem;
  flex: 0 0 auto !important;
  margin-left: auto !important;
  padding-bottom: 0.35rem;
}

.predix-export-btn {
  box-sizing: border-box;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 2.25rem;
  padding: 0 0.9rem;
  margin: 0;
  border: 1px solid rgb(209, 213, 219);
  border-radius: 0.5rem;
  background: #fff;
  color: rgb(55, 65, 81);
  font-family: var(--font-inter, "Inter"), var(--font-sans, ui-sans-serif, system-ui, sans-serif);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
}

.predix-export-btn:hover:not(:disabled) {
  color: var(--v-primary-base, #0393a8);
  border-color: color-mix(in srgb, var(--v-primary-base, #0393a8) 45%, #d1d5db);
  background: color-mix(in srgb, var(--v-primary-base, #0393a8) 8%, #fff);
}

.predix-export-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

html.dark .predix-export-btn {
  background: rgb(255 255 255 / 0.04);
  border-color: rgb(255 255 255 / 0.14);
  color: rgb(209, 213, 219);
  box-shadow: none;
}

html.dark .predix-export-btn:hover:not(:disabled) {
  color: var(--v-primary-base, #0393a8);
  border-color: color-mix(in srgb, var(--v-primary-base, #0393a8) 50%, transparent);
  background: color-mix(in srgb, var(--v-primary-base, #0393a8) 12%, transparent);
}

.predix-export-btn__icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}
</style>
